import React, { useEffect, useMemo } from 'react';
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { BrokenImage } from '@mui/icons-material';
import { ProductExampleAttachmentDto } from 'apiClient/Api';
import { isAttachmentSrc, parseAttachmentId, useAttachmentTokens } from 'Hooks/useAttachmentTokens';

interface MarkdownRendererProps {
  children: string | null | undefined;
  sx?: SxProps<Theme>;
  /** Example id — enables attachment-token resolution for inline refs. */
  exampleId?: string | null;
  /** Attachment list from the example; used to validate `attachment:{id}` refs. */
  attachments?: ProductExampleAttachmentDto[];
}

/**
 * Renders a broken-image placeholder without auto-patching the markdown —
 * editors should resolve the dangling reference manually.
 */
const BrokenAttachment: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      border: 1,
      borderStyle: 'dashed',
      borderColor: 'warning.main',
      color: 'warning.main',
      fontSize: '0.8rem',
    }}
  >
    <BrokenImage fontSize="inherit"/>
    {label}
  </Box>
);

/**
 * Wrapper component for rendering Markdown content with consistent styling.
 * Resolves inline `attachment:{id}` references in images and links through
 * the short-lived access-token flow.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children, sx, exampleId, attachments }) => {
  const tokens = useAttachmentTokens(exampleId ?? null);
  const attachmentIds = useMemo(() => (attachments ?? []).map((a) => a.id!).filter(Boolean), [attachments]);

  useEffect(() => {
    if (!exampleId || attachmentIds.length === 0) return;
    void tokens.prefetch(attachmentIds);
    // prefetch is stable via useCallback; rerun only when the id set changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exampleId, attachmentIds.join('|')]);

  const components = useMemo<Components>(() => ({
    img: ({ src, alt, title }) => {
      const rawSrc = typeof src === 'string' ? src : '';
      if (!isAttachmentSrc(rawSrc)) {
        return <img src={rawSrc} alt={alt ?? ''} title={title}/>;
      }
      const attId = parseAttachmentId(rawSrc);
      const hit = attId ? (attachments ?? []).find((a) => a.id === attId) : undefined;
      if (!attId || !hit) {
        return <BrokenAttachment label={alt || rawSrc}/>;
      }
      const resolved = tokens.resolveNow(attId);
      if (!resolved) {
        return (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              minWidth: 80,
              minHeight: 20,
              bgcolor: 'action.hover',
              borderRadius: 0.5,
              color: 'text.disabled',
              fontSize: '0.75rem',
              px: 0.75,
            }}
          >
            …
          </Box>
        );
      }
      return <img src={resolved} alt={alt ?? hit.fileName ?? ''} title={title ?? hit.fileName ?? undefined}/>;
    },
    a: ({ href, children: linkChildren, title }) => {
      const rawHref = typeof href === 'string' ? href : '';
      if (!isAttachmentSrc(rawHref)) {
        return <a href={rawHref} title={title} target={rawHref.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{linkChildren}</a>;
      }
      const attId = parseAttachmentId(rawHref);
      const hit = attId ? (attachments ?? []).find((a) => a.id === attId) : undefined;
      if (!attId || !hit) {
        return <BrokenAttachment label={typeof linkChildren === 'string' ? linkChildren : rawHref}/>;
      }
      const resolved = tokens.resolveDownloadUrl(attId, true);
      if (!resolved) {
        return <Typography component="span" color="text.disabled">…</Typography>;
      }
      return <a href={resolved} download={hit.fileName ?? undefined} title={title ?? hit.fileName ?? undefined}>{linkChildren}</a>;
    },
  // resolvedVersion triggers re-render when tokens arrive
  }), [attachments, tokens.resolveNow, tokens.resolveDownloadUrl, tokens.resolvedVersion]);

  if (!children) return null;

  return (
    <Box
      sx={{
        '& h1:not([style])': { fontSize: '1.6rem', fontWeight: 600, mt: 0, mb: 1, color: 'text.primary' },
        '& h2:not([style])': { fontSize: '1.35rem', fontWeight: 600, mt: 2, mb: 1, color: 'text.primary' },
        '& h3:not([style])': { fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 0.75, color: 'text.primary' },
        '& h4:not([style])': { fontSize: '1.05rem', fontWeight: 600, mt: 1.5, mb: 0.5, color: 'text.primary' },
        '& h5:not([style])': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5, color: 'text.primary' },
        '& p:not([style])': { mt: 0, mb: 1, color: 'text.primary' },
        '& p:first-of-type:not([style])': { mt: 0 },
        '& p:last-of-type:not([style])': { mb: 0 },
        '& ul:not([style]), & ol:not([style])': { mt: 1, mb: 1, pl: 3, color: 'text.primary' },
        '& li:not([style])': { mb: 0.5, color: 'text.primary' },
        '& code:not([style])': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          px: 0.5, py: 0.125, borderRadius: 0.5, fontSize: '0.875em', fontFamily: 'monospace', color: 'text.primary',
        },
        '& pre:not([style])': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          p: 1.5, borderRadius: 1, overflow: 'auto',
          '& code': { backgroundColor: 'transparent', p: 0 },
        },
        '& blockquote:not([style])': {
          borderLeft: (theme) => `4px solid ${theme.palette.divider}`,
          pl: 2, ml: 0, fontStyle: 'italic', color: 'text.secondary',
        },
        '& a': {
          '&:not([style*="color"])': { color: 'primary.main' },
          '&:not([style*="text-decoration"])': {
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          },
        },
        '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
        '& table:not([style])': { borderCollapse: 'collapse', width: '100%', my: 1 },
        '& th:not([style]), & td:not([style])': {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          p: 1, textAlign: 'left',
        },
        '& th:not([style])': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          fontWeight: 600,
        },
        '& hr:not([style])': { border: 'none', borderTop: (theme) => `1px solid ${theme.palette.divider}`, my: 2 },
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
        urlTransform={(url) => url.startsWith('attachment:') ? url : defaultUrlTransform(url)}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};
